import {
  Directive,
  forwardRef,
  input,
  InputSignal,
  effect
} from '@angular/core';
import {
  NG_VALIDATORS
} from '@angular/forms';
import type {
  Provider,
  Type
} from '@angular/core';
import type {
  AbstractControl,
  ValidationErrors,
  Validator
} from '@angular/forms';
import type { SelectedFile } from './file-upload.component';

export const FILE_UPLOAD_SIZE_VALIDATOR: Provider = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef((): Type<FileUploadSizeValidator> => FileUploadSizeValidator),
  multi: true
};

/**
 * FileUploadSizeValidator Directive
 *
 * Validates the size of a single file added in a file input in bytes
 *
 * @example
 * <td-wb-file-upload [tdWbFileUploadSizeValidator]="1000000" [(ngModel)]="files"></td-wb-file-upload>
 */
@Directive({
  selector: '[tdWbFileUploadSizeValidator][ngModel]',
  providers: [FILE_UPLOAD_SIZE_VALIDATOR]
})
export class FileUploadSizeValidator implements Validator {
  private onChange: () => void;

  readonly size: InputSignal<number | undefined> = input<number | undefined>(undefined, { alias: 'tdWbFileUploadSizeValidator' });
  private _prevSize: number | undefined;

  constructor() {
    // notify Angular forms validator when the input binding changes
    effect(() => {
      const s = this.size();
      if (s !== this._prevSize && this.onChange !== undefined) {
        this.onChange();
      }
      this._prevSize = s;
    });
  }

  validate(fileUploadControl: AbstractControl): ValidationErrors | null {
    const size = this.size();
    if (size !== undefined && fileUploadControl.value !== undefined && fileUploadControl.value !== null) {
      return this.validateSize(fileUploadControl.value, size);
    }
    return null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onChange = fn;
  }

  private validateSize(files: Array<SelectedFile>, size: number): ValidationErrors | null {
    const result = files.find((file: SelectedFile) => file.file.size > size);
    return result !== undefined ? { SINGLE_SIZE: true } : null;
  }
}
